//---------------------------------------------------------
// Soft provision DTOs (contract 0007 + Soft catalog expansion + multi-account)
//---------------------------------------------------------
export type SoftMode = 'nat_onearm_nonha' | 'nat_onearm_ha' | 'dsr_nonha' | 'dsr_ha';
export type SoftNetworkMode = 'legacy_private' | 'nat_vpc' | 'nat_vlan' | 'dsr_vlan';
export type SoftJobStatus = 'queued' | 'running' | 'succeeded' | 'failed';

/** One Linode account. List/create/replace never include the raw token. username is who saved it. */
export interface SoftLinodeAccount {
	id: string;
	label: string;
	tokenLast4: string;
	username: string;
	updatedAt: string;
}

/** One saved public key. List and create never include the key body. */
export interface SoftSSHKey {
	id: string;
	label: string;
	username: string;
}

/** @deprecated Singleton settings shape removed; use SoftLinodeAccount. */
export type SoftLinodeSettings = SoftLinodeAccount;

export interface SoftTemplate {
	id: string;
	name: string;
	mode: SoftMode | string;
	osVariants: string[];
	script?: string;
	description?: string;
	updatedAt: string;
	createdAt?: string;
}

export interface SoftProvisionJob {
	jobId: string;
	status: SoftJobStatus | string;
	mode: string;
	osVariant: string;
	imageId?: string;
	region: string;
	label?: string;
	planType?: string;
	networkMode?: SoftNetworkMode | string;
	templateId?: string;
	accountId?: string;
	firewallId?: number;
	linodeId?: number;
	publicIp?: string;
	privateIp?: string;
	instanceId?: number;
	error?: {code: string; message: string};
	warnings?: string[];
	/** HA jobs (mode nat_onearm_ha) only. */
	ha?: boolean;
	pairId?: string | null;
	step?: string | null;
	steps?: {step: 'creating_node_1' | 'creating_node_2' | 'sharing_address' | 'ready'; state: 'pending' | 'running' | 'done' | 'failed' | 'not_reached'}[];
	failedStep?: string | null;
	failedNode?: number | null;
	nodes?: {node: number; linodeId?: number; publicIp?: string; privateIp?: string}[];
	createdAt: string;
	updatedAt: string;
}

export interface SoftCatalogType {
	id: string;
	label: string;
	class?: string;
	vcpus?: number;
	memoryMb?: number;
	gpus?: number;
}

export interface SoftCatalogRegion {
	id: string;
	label: string;
	country?: string;
	capabilities?: string[];
}

export interface SoftCatalogImage {
	id: string;
	label: string;
	vendor?: string;
	capabilities?: string[];
}

/** Catalog firewall row. cp11111 is missing, restricted, or open. Do not show that word in the UI. */
export interface SoftCatalogFirewall {
	id: number;
	label: string;
	cp11111: 'missing' | 'restricted' | 'open';
}

/** POST /oam/provision/jobs. Exactly one of osVariant/imageId and one of firewallId/firewallLabel. */
export interface SoftProvisionJobCreateRequest {
	templateId: string;
	osVariant?: string;
	imageId?: string;
	region: string;
	mode: string;
	accountId: string;
	sshKeyId: string;
	label?: string;
	planType?: string;
	networkMode?: string;
	firewallId?: number;
	firewallLabel?: string;
	vlan?: {label: string; address: string};
	vpc?: {vpcId?: number; subnetId: number};
}

/** Soft-side fallback labels only when catalog empty (CoS preselects still apply). */
export const SOFT_OS_VARIANTS = ['linode/debian13', 'linode/ubuntu24.04', 'debian13', 'ubuntu24.04'] as const;
export const SOFT_REGIONS = ['sg-sin-2'] as const;
export const SOFT_PLAN_TYPES = ['g6-dedicated-8', 'g8-dedicated-64-32'] as const;
export const SOFT_DEFAULT_PLAN = 'g6-dedicated-8';
export const SOFT_DEFAULT_REGION = 'sg-sin-2';
export const SOFT_DEFAULT_OS = 'linode/debian13';
export const SOFT_MODE: SoftMode = 'nat_onearm_nonha';
export const SOFT_NETWORK_MODE_LEGACY: SoftNetworkMode = 'legacy_private';
export const SOFT_MODE_HA: SoftMode = 'nat_onearm_ha';
/** Template modes an admin can save. NAT and direct-return, single and HA. */
export const SOFT_TEMPLATE_MODES: {id: SoftMode; label: string}[] = [
	{id: 'nat_onearm_nonha', label: 'NAT, single load balancer'},
	{id: 'nat_onearm_ha', label: 'NAT, high availability pair'},
	{id: 'dsr_nonha', label: 'DR, single load balancer'},
	{id: 'dsr_ha', label: 'DR, high availability pair'},
];
/** UX lock #50: four labels; only stamped modes are enabled. The server 422 is the control. */
export const SOFT_NETWORK_MODES: {id: SoftNetworkMode; label: string; disabled: boolean; family: 'nat' | 'dsr'}[] = [
	{id: 'legacy_private', label: 'NAT, private network', disabled: false, family: 'nat'},
	{id: 'nat_vpc', label: 'NAT, VPC', disabled: false, family: 'nat'},
	{id: 'nat_vlan', label: 'NAT, VLAN', disabled: false, family: 'nat'},
	{id: 'dsr_vlan', label: 'DR, VLAN', disabled: false, family: 'dsr'},
];

/** @deprecated SoftPlanType — planType is now any catalog slug string. */
export type SoftPlanType = string;
export type SoftOSVariant = string;
export type SoftRegion = string;
